import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Picture } from "@/components/site/Picture";
import { Section } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { LazyUSMarketsMap as USMarketsMap } from "@/components/site/LazyUSMarketsMap";
import { StatsBand } from "@/components/site/StatsBand";

import { CTAButton } from "@/components/site/Button";
import { ArrowUpRight, MapPin, Handshake, TrendingUp } from "lucide-react";
import heroAz from "@/assets/hero-az.jpg";

export const Route = createFileRoute("/kcn-worldwide")({
  head: () => ({
    meta: [
      { title: "Top Real Estate Teams in Arizona and Nationwide | KCN" },
      {
        name: "description",
        content:
          `From Keller Williams Scottsdale to Keller Williams Alaska, KCN spans ${STATS_TEXT.markets} markets. Find one of the top real estate teams in Arizona and nationwide to join.`,
      },
      { property: "og:title", content: "KCN Worldwide - Locations" },
      {
        property: "og:description",
        content:
          `${STATS_TEXT.markets} KCN markets across the U.S. - Alaska, Arizona, California, Florida, Kansas, Michigan, Nebraska and Wisconsin.`,
      },
      { property: "og:url", content: "https://joinkcn.com/kcn-worldwide" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/kcn-worldwide" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Kristan Cole Network markets",
          itemListElement: locations.map((loc, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "RealEstateAgent",
              name: `Kristan Cole Network - ${loc.name}`,
              areaServed: { "@type": "Place", name: loc.name },
              description: `KCN real estate team serving ${loc.region}.`,
              url: loc.url,
              parentOrganization: {
                "@type": "Organization",
                name: "Kristan Cole Network",
                url: "https://joinkcn.com/",
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "KCN Worldwide",
              item: "https://joinkcn.com/kcn-worldwide",
            },
          ],
        }),
      },
      faqSchema(LOCATIONS_FAQS, "https://joinkcn.com/kcn-worldwide"),
    ],
  }),
  component: LocationsPage,
});

import { locations } from "@/lib/locations";
import { MARKETS_COUNT, STATS_TEXT } from "@/lib/stats";
import { DirectAnswer, FAQSection, faqSchema } from "@/components/site/AnswerBlock";
import { LOCATIONS_ANSWER, LOCATIONS_FAQS } from "@/lib/aeo";


function LocationsPage() {
  return (
    <SiteLayout>
      {/* FULL-BLEED HERO */}
      <section className="relative isolate min-h-[85vh] flex items-end overflow-hidden">
        <Picture
          src={heroAz}
          alt="Aerial view of an American real estate market at golden hour"
          pictureClassName="absolute inset-0 block h-full w-full"
          className="h-full w-full object-cover"
          width={1920}
          height={1280}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/15" />
        <div className="relative container-edge pb-14 md:pb-16 pt-36 w-full">
          <p className="eyebrow text-foreground/90 mb-8">KCN Worldwide</p>
          <h1 className="headline-xl max-w-5xl">
            One network. {MARKETS_COUNT}+ markets.
            <br />
            <span className="serif-italic text-foreground/85">Zero ceiling.</span>
          </h1>

          <p className="mt-8 lede max-w-2xl text-foreground/90">
            From Alaska to Florida, Arizona to Wisconsin - KCN agents run the
            same playbook in every market. Find the team nearest you.
          </p>
        </div>
      </section>

      {/* STATS ABOVE MAP - one full-bleed band */}
      <section className="bg-background border-b border-border/60">
        <div className="container-edge pt-12 md:pt-16 pb-10">
          <StatsBand variant="static" />

        </div>

        <div className="container-edge pb-8 md:pb-10">
          <div className="text-center mb-16">
            <p className="eyebrow mb-6">The Footprint</p>
            <h2 className="headline-lg">
              Coast to coast.{" "}
              <span className="serif-italic text-primary">And then some.</span>
            </h2>
          </div>
          <USMarketsMap
            markers={locations.map((l) => ({
              name: l.name,
              region: l.region,
              url: l.url,
              coordinates: l.coords,
            }))}
          />
        </div>
      </section>

      {/* GRID */}
      <Section tone="dark" className="pt-6 md:pt-8">

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border bg-background">
          {locations.map((loc) => (
            <a
              key={loc.name}
              href={loc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-end justify-between gap-4 border-b border-r border-border bg-background p-7 md:p-8 min-h-[150px] transition-colors hover:bg-surface"
            >
              <div>
                <h3 className="font-display text-2xl md:text-3xl leading-tight text-foreground group-hover:text-primary transition-colors">
                  {loc.name}
                </h3>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-foreground/60">
                  {loc.region}
                </p>
              </div>
              <ArrowUpRight className="h-5 w-5 text-foreground/50 shrink-0 transition-all duration-300 group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          ))}
        </div>


      </Section>

      {/* EXPANSION PARTNERS */}
      <section className="relative border-y-2 border-primary bg-surface">
        <div className="container-edge py-14 md:py-18">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-primary/60 bg-primary/10 mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inset-0 rounded-full bg-primary animate-ping opacity-75" />
                  <span className="relative rounded-full h-2 w-2 bg-primary" />
                </span>
                <span className="text-xs uppercase tracking-[0.22em] text-primary font-medium">
                  Now Seeking Expansion Partners
                </span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">
                {MARKETS_COUNT}+ markets today.
                <br />
                <span className="serif-italic text-primary">Your city next.</span>
              </h2>
              <p className="text-foreground/90 text-lg leading-[1.7] max-w-2xl mb-6">
                KCN is actively expanding. We're looking for proven team leaders and
                top-producing agents who want to plant the KCN flag in their market - 
                with our systems, coaching, and the full weight of the network behind them.
              </p>
              <p className="text-foreground/75 leading-[1.7] max-w-2xl">
                If you've ever thought, "I could lead this in my city" - we should talk.
              </p>
            </div>

            <div className="lg:col-span-5 space-y-5">
              <div className="flex gap-4 p-5 border border-border bg-background">
                <Handshake className="h-6 w-6 text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-xl mb-1">Partner, not franchisee</h3>
                  <p className="text-sm text-foreground/75 leading-relaxed">
                    Keep your local brand equity. Plug into KCN's playbook, tech, and referral network.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-5 border border-border bg-background">
                <TrendingUp className="h-6 w-6 text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-xl mb-1">Built for scale</h3>
                  <p className="text-sm text-foreground/75 leading-relaxed">
                    The same systems running {MARKETS_COUNT}+ markets - handed to you on day one.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-5 border border-border bg-background">
                <MapPin className="h-6 w-6 text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-xl mb-1">Open territories</h3>
                  <p className="text-sm text-foreground/75 leading-relaxed">
                    Most U.S. metros are still open. First mover in your market wins.
                  </p>
                </div>
              </div>
              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <CTAButton href="/round-table" variant="primary">
                  Request an invitation
                </CTAButton>
                <CTAButton href="/strategy-call" variant="outline">
                  Book a strategy call
                </CTAButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DirectAnswer question={LOCATIONS_ANSWER.question} answer={LOCATIONS_ANSWER.answer} />

      <FAQSection
        faqs={LOCATIONS_FAQS}
        eyebrow="Questions about markets"
        heading="Joining KCN in your market."
      />

      <CTABanner cta="roundtable" />
    </SiteLayout>
  );
}
